import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';

/**
 * The keypad the counter types on.
 *
 * Drawn by the page rather than raised by the operating system, which is the
 * whole reason the counter exists: a keyboard the page owns cannot slide away,
 * cannot cover the thing being typed into, and cannot resize the viewport out
 * from under a thumb that is already moving.
 *
 * It also carries keys a phone keypad does not have. `+` and `×` because
 * counting stock means adding up part-full containers and multiplying identical
 * packs. `00` because gram counts cluster on round hundreds.
 *
 * And the unit key, which is why there is no decimal point anywhere: 3 kg is
 * typed as "3" then kg, never as "3.0" or "3000". A decimal point on a field
 * that overwrites stock is where the ten-fold errors live.
 *
 * Two keys change their meaning rather than their position, so the thumb never
 * has to travel and the label always states what the press will do:
 *   NEXT becomes SKIP when nothing has been typed.
 *   ⌫ becomes BACK when there is nothing left to delete.
 */

const KEY_HEIGHT = 52;

function Key(props) {
    const { children, onPress, variant, span, sx } = props;
    return (
        <Button
            variant={variant || 'outlined'}
            onClick={onPress}
            // Keeps focus on the entry line, so a hardware keyboard still works
            // and the caret never jumps into a button.
            onMouseDown={(event) => event.preventDefault()}
            sx={{
                minWidth: 0, height: span ? 'auto' : KEY_HEIGHT, p: 0,
                gridRow: span ? `span ${span}` : undefined,
                fontSize: '1.15rem', fontWeight: 600, lineHeight: 1,
                fontVariantNumeric: 'tabular-nums',
                ...sx,
            }}
        >
            {children}
        </Button>
    );
}

function CountKeypad(props) {
    const { onDigit, onOperator, onDelete, onBack, onNext, onUnit,
        canDelete, isEmpty, units, unit } = props;

    // A second unit is offered only where one exists - pieces have none.
    const other = units.find((choice) => choice.label !== unit.label);

    return (
        <Box sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: 0.75,
            p: 1,
        }}>
            {['7', '8', '9'].map((digit) => (
                <Key key={digit} onPress={() => onDigit(digit)}>{digit}</Key>
            ))}
            <Key onPress={canDelete ? onDelete : onBack}
                sx={{ fontSize: canDelete ? '1.15rem' : '0.8rem', color: 'text.secondary' }}>
                {canDelete ? '⌫' : '‹ Back'}
            </Key>

            {['4', '5', '6'].map((digit) => (
                <Key key={digit} onPress={() => onDigit(digit)}>{digit}</Key>
            ))}
            <Key onPress={() => onOperator('+')} sx={{ color: 'primary.main' }}>+</Key>

            {['1', '2', '3'].map((digit) => (
                <Key key={digit} onPress={() => onDigit(digit)}>{digit}</Key>
            ))}
            {/* The largest target on the screen, bottom right, under the thumb. */}
            <Key onPress={onNext} span={2}
                variant={isEmpty ? 'outlined' : 'contained'}
                sx={{ fontSize: '0.85rem', textTransform: 'uppercase',
                    letterSpacing: '.04em', fontWeight: 700 }}>
                {isEmpty ? 'Skip ›' : 'Next ›'}
            </Key>

            <Key onPress={() => onDigit('0')}>0</Key>
            <Key onPress={() => onDigit('00')} sx={{ fontSize: '1rem' }}>00</Key>
            {other ? (
                <Key onPress={() => onUnit(other)}
                    sx={{ fontSize: '0.95rem', color: 'primary.main', fontWeight: 700 }}>
                    {other.label}
                </Key>
            ) : (
                <Key onPress={() => onOperator('*')} sx={{ color: 'primary.main' }}>×</Key>
            )}
        </Box>
    );
}

export default CountKeypad;
